import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import UploadIcon from '@mui/icons-material/FileUploadOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import LineItemsDialog from '@/components/common/LineItemsDialog';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { WarehouseDto } from '@/types/organization';
import type { ClientDto, MaterialDto, PagedResult } from '@/types/materials';
import type {
  CreateProductionOrderRequest,
  ProductionOrderDto,
  ProductionOrderLineDto,
  ProductionOrderLineInput,
  UpdateProductionOrderRequest,
} from '@/types/production';

type PlanMode = 'Bom' | 'Manual' | 'Excel';

const EMPTY_LINE: ProductionOrderLineInput = { materialId: '', requiredQty: 0 };

export default function ProductionOrdersPage() {
  const { t } = useTranslation('production');
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: config } = useProcessConfiguration();
  // Which mode(s) the screen offers is admin-configurable (Process Configuration > Production) —
  // choosing one, two, or all three. If a company somehow disables all three, fall back to BOM
  // Request rather than rendering a dialog with no usable mode at all.
  const availableModes = (['Bom', 'Manual', 'Excel'] as PlanMode[]).filter((m) => {
    if (m === 'Bom') return config?.productionPlanBomEnabled ?? true;
    if (m === 'Manual') return config?.productionPlanManualEnabled ?? true;
    return config?.productionPlanExcelImportEnabled ?? true;
  });
  if (availableModes.length === 0) availableModes.push('Bom');

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['production-orders'],
    queryFn: async () => (await apiClient.get<ProductionOrderDto[]>('/production-orders')).data,
  });
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<WarehouseDto[]>('/warehouses')).data,
  });
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  const materials = materialsPage?.items ?? [];
  // Only FG/SFG materials can be produced — see MaterialCategory.Type.
  const producibleMaterials = materials.filter((m) => m.materialType === 'FG' || m.materialType === 'SFG');
  // Only RM/SFG materials are valid production inputs — mirrors BomService's own component
  // validation (an FG can't itself be a component of producing another FG/SFG).
  const componentMaterials = materials.filter((m) => m.materialType === 'RM' || m.materialType === 'SFG');
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => (await apiClient.get<ClientDto[]>('/clients')).data,
    enabled: config?.thirdPartyLogisticsEnabled ?? false,
  });

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<ProductionOrderDto | null>(null);
  const [mode, setMode] = useState<PlanMode>('Bom');
  const [materialId, setMaterialId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [clientId, setClientId] = useState('');
  const [plannedQty, setPlannedQty] = useState(0);
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState<ProductionOrderLineInput[]>([EMPTY_LINE]);
  const [excelErrors, setExcelErrors] = useState<string[]>([]);

  const resetForm = () => {
    setEditingId(null);
    setMode(availableModes[0]);
    setMaterialId('');
    setWarehouseId('');
    setClientId('');
    setPlannedQty(0);
    setRemarks('');
    setLines([EMPTY_LINE]);
    setExcelErrors([]);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['production-orders'] });

  // Only usable while Status is still "Draft" — mirrors ProductionOrderService.UpdateAsync's guard.
  // Material/creation-mode aren't editable — a BOM-mode order always re-explodes its BOM; a
  // Manual/Excel-mode order (bomId null) keeps whichever line list it was created with, editable here.
  const openEdit = (r: ProductionOrderDto) => {
    setEditingId(r.id);
    setMaterialId(r.materialId);
    setWarehouseId(r.warehouseId);
    setClientId(r.clientId ?? '');
    setPlannedQty(r.plannedQty);
    setRemarks(r.remarks ?? '');
    setExcelErrors([]);
    if (r.bomId) {
      setMode('Bom');
      setLines([EMPTY_LINE]);
    } else {
      setMode('Manual');
      setLines(r.lines.length > 0 ? r.lines.map((l) => ({ materialId: l.materialId, requiredQty: l.requiredQty })) : [EMPTY_LINE]);
    }
    setOpen(true);
  };

  const handleDownloadTemplate = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([['Material Code', 'Material Name', 'Qty']]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Production Plan');
    XLSX.writeFile(workbook, 'production-plan-template.xlsx');
  };

  // Parses an uploaded workbook into the same line-list state Manual mode edits directly — the
  // operator reviews/adjusts the parsed rows before submitting, same as a BOM explosion's result.
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet);

    const errors: string[] = [];
    const parsedLines: ProductionOrderLineInput[] = [];
    rows.forEach((row, i) => {
      const code = String(row['Material Code'] ?? '').trim();
      if (!code) return;
      const qty = Number(row['Qty'] ?? 0);
      const material = componentMaterials.find((m) => m.code.toLowerCase() === code.toLowerCase());
      if (!material) {
        errors.push(t('form.excelRowMaterialNotFound', { row: i + 2, code }));
        return;
      }
      if (!(qty > 0)) {
        errors.push(t('form.excelRowInvalidQty', { row: i + 2, code }));
        return;
      }
      parsedLines.push({ materialId: material.id, requiredQty: qty });
    });

    setExcelErrors(errors);
    setLines(parsedLines.length > 0 ? parsedLines : [EMPTY_LINE]);
  };

  const cleanLines = lines.filter((l) => l.materialId && l.requiredQty > 0);
  const linesValid = mode === 'Bom' || cleanLines.length > 0;

  const createMutation = useMutation({
    mutationFn: () => {
      const linesPayload = mode === 'Bom' ? undefined : cleanLines;
      const resolvedClientId = config?.thirdPartyLogisticsEnabled ? clientId || null : null;
      if (editingId) {
        const payload: UpdateProductionOrderRequest = {
          warehouseId, plannedQty, plannedDate: null, remarks: remarks || null, lines: linesPayload, clientId: resolvedClientId,
        };
        return apiClient.put(`/production-orders/${editingId}`, payload);
      }
      const payload: CreateProductionOrderRequest = {
        materialId, warehouseId, plannedQty, plannedDate: null, remarks: remarks || null, lines: linesPayload, clientId: resolvedClientId,
      };
      return apiClient.post('/production-orders', payload);
    },
    onSuccess: () => {
      invalidate();
      setOpen(false);
    },
  });

  const releaseMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/production-orders/${id}/release`),
    onSuccess: invalidate,
  });

  const closeMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/production-orders/${id}/close`),
    onSuccess: invalidate,
  });

  const columns: DataTableColumn<ProductionOrderDto>[] = [
    { field: 'orderNumber', headerName: t('table.number') },
    { field: 'materialCode', headerName: t('table.material'), render: (r) => `${r.materialCode} — ${r.materialName}`, filterable: true },
    { field: 'bomNumber', headerName: t('table.bom'), render: (r) => r.bomNumber ?? t('form.modeManualShort') },
    { field: 'warehouseName', headerName: t('table.warehouse'), filterable: true },
    { field: 'plannedQty', headerName: t('table.plannedQty') },
    { field: 'producedQty', headerName: t('table.producedQty') },
    { field: 'status', headerName: t('table.status'), render: (r) => (r.isClosed ? <StatusChip status="Closed" /> : <StatusChip status={r.status} />), filterable: true },
    ...(config?.thirdPartyLogisticsEnabled
      ? [{ field: 'clientName', headerName: t('table.client'), render: (r: ProductionOrderDto) => r.clientName ?? '—', filterable: true } as DataTableColumn<ProductionOrderDto>]
      : []),
    {
      field: 'action', headerName: t('table.actions'), render: (r) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={() => setViewing(r)}>
            {t('view')}
          </Button>
          <IfPermitted permission={PERMISSIONS.PRODUCTION_MANAGE}>
            {r.status === 'Draft' && (
              <>
                <Button size="small" onClick={() => openEdit(r)}>
                  {t('edit')}
                </Button>
                <Button size="small" variant="outlined" disabled={releaseMutation.isPending} onClick={() => releaseMutation.mutate(r.id)}>
                  {t('release')}
                </Button>
              </>
            )}
            {r.status !== 'Completed' && !r.isClosed && (
              <Button size="small" color="error" disabled={closeMutation.isPending} onClick={() => closeMutation.mutate(r.id)}>
                {t('close')}
              </Button>
            )}
          </IfPermitted>
        </Stack>
      ),
    },
  ];

  const lineColumns: DataTableColumn<ProductionOrderLineDto>[] = [
    { field: 'materialCode', headerName: t('table.material'), render: (l) => `${l.materialCode} — ${l.materialName}` },
    { field: 'materialType', headerName: t('table.type'), render: (l) => l.materialType ?? '—' },
    { field: 'requiredQty', headerName: t('table.requiredQty') },
    { field: 'issuedQty', headerName: t('table.issuedQty') },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.PRODUCTION_MANAGE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpen(true);
              }}
            >
              {t('add')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={orders} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="production-orders" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? t('edit') : t('add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Which modes appear here is admin-configurable (Process Configuration > Production) —
                hidden entirely when only one mode is enabled, since there's nothing to toggle. */}
            {availableModes.length > 1 && (
              <ToggleButtonGroup
                color="primary"
                exclusive
                fullWidth
                size="small"
                value={mode}
                disabled={Boolean(editingId)}
                onChange={(_, value: PlanMode | null) => {
                  if (!value) return;
                  setMode(value);
                  setExcelErrors([]);
                  if (value !== 'Bom' && lines.length === 0) setLines([EMPTY_LINE]);
                }}
              >
                {availableModes.map((m) => (
                  <ToggleButton key={m} value={m}>
                    {t(`form.mode${m}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}

            <SearchableSelect
              label={t('form.material')} value={materialId} onChange={setMaterialId} fullWidth
              helperText={mode === 'Bom' ? t('form.materialHint') : t('form.materialHintNoBom')}
              disabled={Boolean(editingId)}
              options={producibleMaterials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name} (${m.materialType})` }))}
            />
            <SearchableSelect
              label={t('form.warehouse')} value={warehouseId} onChange={setWarehouseId} fullWidth
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
            />
            {config?.thirdPartyLogisticsEnabled && (
              <SearchableSelect
                label={t('form.client')} value={clientId} onChange={setClientId} fullWidth
                options={clients.map((c) => ({ value: c.id, label: c.name }))}
              />
            )}
            <TextField type="number" label={t('form.plannedQty')} value={plannedQty} onChange={(e) => setPlannedQty(Number(e.target.value))} fullWidth />
            <TextField label={t('form.remarks')} value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth multiline minRows={2} />

            {mode !== 'Bom' && (
              <>
                <Divider />

                {mode === 'Excel' && (
                  <Stack spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                      {t('form.excelHint')}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" startIcon={<DownloadIcon fontSize="small" />} onClick={handleDownloadTemplate}>
                        {t('form.downloadTemplate')}
                      </Button>
                      <Button size="small" variant="outlined" component="label" startIcon={<UploadIcon fontSize="small" />}>
                        {t('form.uploadFile')}
                        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFileUpload} />
                      </Button>
                    </Stack>
                    {excelErrors.length > 0 && (
                      <Alert severity="error">
                        <Stack spacing={0.25}>
                          {excelErrors.map((err, i) => (
                            <Typography key={i} variant="body2">
                              {err}
                            </Typography>
                          ))}
                        </Stack>
                      </Alert>
                    )}
                  </Stack>
                )}

                <Typography variant="subtitle2">{t('form.componentLines')}</Typography>
                {lines.map((line, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="center">
                    <SearchableSelect
                      label={t('form.material')} value={line.materialId} fullWidth size="small"
                      onChange={(value) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, materialId: value } : l)))}
                      options={componentMaterials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name} (${m.materialType})` }))}
                    />
                    <TextField
                      type="number" label={t('form.quantity')} value={line.requiredQty} size="small" sx={{ minWidth: 120 }}
                      onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, requiredQty: Number(e.target.value) } : l)))}
                    />
                    <IconButton size="small" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} disabled={lines.length <= 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button size="small" startIcon={<AddIcon />} onClick={() => setLines((prev) => [...prev, EMPTY_LINE])} sx={{ alignSelf: 'flex-start' }}>
                  {t('form.addLine')}
                </Button>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button
            variant="contained"
            disabled={!materialId || !warehouseId || plannedQty <= 0 || !linesValid || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <LineItemsDialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing?.orderNumber ?? ''}
        subtitle={viewing ? `${viewing.materialCode} — ${viewing.materialName} · ${t('form.plannedQty')}: ${viewing.plannedQty}` : undefined}
        columns={lineColumns}
        rows={viewing?.lines ?? []}
      />
    </>
  );
}
